import { useCallback, useEffect, useState } from 'react';

import { getQueryStringValue, setQueryStringValue } from './useQueryString';

/**
 * Combines useQueryString and useLocalStorage:
 * - On init: URL param takes priority over localStorage, which takes priority over initialValue.
 * - On set: syncs to both the URL query string and localStorage so the value survives page navigation.
 *
 * Drop-in replacement for useQueryString wherever persistence across navigation is needed.
 */
export const useLocalStorageWithURL = (key: string, initialValue?: any, addHistoryRecord?: boolean) => {
  const [value, setValue] = useState(() => {
    const urlValue = getQueryStringValue(key);
    if (urlValue !== null && urlValue !== undefined) {
      return urlValue;
    }
    try {
      const stored = window.localStorage.getItem(key);
      return stored ? JSON.parse(stored) : initialValue;
    } catch {
      return initialValue;
    }
  });

  // On mount, write the restored value back into the URL if the URL lacks the param.
  // This makes the link immediately shareable after SPA navigation back to the page.
  useEffect(() => {
    const urlValue = getQueryStringValue(key);
    if (value !== null && value !== undefined && (urlValue === null || urlValue === undefined)) {
      setQueryStringValue(key, value, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = useCallback(
    (newValue: any) => {
      setValue(newValue);
      setQueryStringValue(key, newValue, addHistoryRecord);
      try {
        window.localStorage.setItem(key, JSON.stringify(newValue));
      } catch (error) {
        console.error(error);
      }
    },
    [key, addHistoryRecord],
  );

  return [value, set] as const;
};
